(() => {
  const sectionSelector = '.home-reviews';
  const bodySelector = '.jdgm-carousel-item__review-body';
  const productTitleSelector = '.jdgm-carousel-item__product-title';
  const productVendorRequests = new Map();
  const visibleCardCount = 6;

  function removeInvalidReviews(root) {
    root.querySelectorAll('.jdgm-carousel-item').forEach((card) => {
      const reviewText = card.querySelector(bodySelector)?.textContent.trim() || '';
      const meaningfulText = reviewText.replace(/[^\p{L}\p{N}]+/gu, '');
      if (meaningfulText.length < 2) card.remove();
    });
  }

  function enhanceReview(body, section) {
    if (body.closest('.jdgm-carousel-item')?.querySelector('.jdgm-carousel-item__review-more')) return;

    body.dataset.readMoreReady = 'true';
    const card = body.closest('.jdgm-carousel-item');
    if (!card) return;

    requestAnimationFrame(() => {
      const textContainer = body.firstElementChild || body;
      const isOverflowing = textContainer.scrollHeight > textContainer.clientHeight + 1;
      const isLongReview = body.textContent.trim().length >= 100;
      if (!isOverflowing && !isLongReview) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'jdgm-carousel-item__review-more';
      button.textContent = section.dataset.reviewMoreLabel || 'Lees meer';
      button.setAttribute('aria-expanded', 'false');
      button.addEventListener('click', () => {
        const expanded = card.classList.toggle('is-review-expanded');
        const wrapper = card.closest('.jdgm-carousel__item-wrapper');
        wrapper?.classList.toggle('has-expanded-review', Boolean(wrapper.querySelector('.is-review-expanded')));
        button.textContent = expanded
          ? (section.dataset.reviewLessLabel || 'Lees minder')
          : (section.dataset.reviewMoreLabel || 'Lees meer');
        button.setAttribute('aria-expanded', String(expanded));
      });
      body.insertAdjacentElement('afterend', button);
    });
  }

  function enhanceReviews(root, section) {
    root.querySelectorAll(bodySelector).forEach((body) => enhanceReview(body, section));
  }

  function parseVendors(section) {
    const source = section.querySelector('[data-review-vendors]');
    if (!source) return [];

    try {
      return JSON.parse(source.textContent)
        .map((vendor) => String(vendor).trim())
        .filter(Boolean)
        .sort((first, second) => second.length - first.length);
    } catch (error) {
      return [];
    }
  }

  function productTitleWithoutVendor(fullTitle, vendor) {
    const normalizedTitle = fullTitle.toLocaleLowerCase();
    if (!normalizedTitle.startsWith(vendor.toLocaleLowerCase())) return fullTitle;

    let boundaryIndex = vendor.length;
    const trademark = fullTitle.slice(boundaryIndex, boundaryIndex + 1);
    if (trademark === '®' || trademark === '™') boundaryIndex += 1;
    const boundary = fullTitle.slice(boundaryIndex, boundaryIndex + 1);
    if (boundary && !/[\s\-–|:]/.test(boundary)) return fullTitle;

    return fullTitle.slice(boundaryIndex).trim().replace(/^[\-–|:]\s*/, '').trim() || fullTitle;
  }

  function renderProductTitle(titleElement, fullTitle, vendor) {
    const productTitle = productTitleWithoutVendor(fullTitle, vendor);

    const vendorElement = document.createElement('span');
    vendorElement.className = 'home-review-product__vendor';
    vendorElement.textContent = vendor;

    const productElement = document.createElement('span');
    productElement.className = 'home-review-product__title';
    productElement.textContent = productTitle;

    titleElement.textContent = '';
    titleElement.append(vendorElement, productElement);
    titleElement.closest('a')?.setAttribute('aria-label', fullTitle);
  }

  function fetchProductVendor(titleElement) {
    const productLink = titleElement.closest('a');
    const handle = productLink?.pathname.match(/\/products\/([^/]+)/)?.[1];
    if (!handle) return Promise.resolve('');

    if (!productVendorRequests.has(handle)) {
      const root = window.Shopify?.routes?.root || '/';
      productVendorRequests.set(
        handle,
        fetch(`${root}products/${handle}.js`)
          .then((response) => response.ok ? response.json() : null)
          .then((product) => String(product?.vendor || '').trim())
          .catch(() => '')
      );
    }

    return productVendorRequests.get(handle);
  }

  function enhanceProductTitles(root, vendors) {
    root.querySelectorAll(productTitleSelector).forEach((titleElement) => {
      const fullTitle = titleElement.textContent.trim();
      if (!fullTitle) return;

      const vendor = vendors.find((candidate) => productTitleWithoutVendor(fullTitle, candidate) !== fullTitle);
      if (vendor) {
        renderProductTitle(titleElement, fullTitle, vendor);
        return;
      }

      fetchProductVendor(titleElement).then((productVendor) => {
        if (productVendor && titleElement.isConnected) {
          renderProductTitle(titleElement, fullTitle, productVendor);
        }
      });
    });
  }

  function loadVisibleImages(root) {
    root.querySelectorAll('.jdgm-carousel-item:not([hidden]) img[data-src]').forEach((image) => {
      const source = window.devicePixelRatio > 1 && image.dataset.srcRetina
        ? image.dataset.srcRetina
        : image.dataset.src;

      if (!source) return;

      image.src = source;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.removeAttribute('data-src');
      image.removeAttribute('data-src-retina');
    });
  }

  function enhanceSlider(root, section) {
    const container = root.querySelector('.jdgm-carousel__item-container');
    const previous = root.querySelector('.jdgm-carousel__left-arrow');
    const next = root.querySelector('.jdgm-carousel__right-arrow');
    if (!container || !previous || !next) return;

    previous.setAttribute('role', 'button');
    previous.setAttribute('tabindex', '0');
    previous.setAttribute('aria-label', section.dataset.reviewPreviousLabel || 'Vorige reviews');
    next.setAttribute('role', 'button');
    next.setAttribute('tabindex', '0');
    next.setAttribute('aria-label', section.dataset.reviewNextLabel || 'Volgende reviews');

    const updateButtons = () => {
      previous.setAttribute('aria-disabled', String(container.scrollLeft <= 1));
      next.setAttribute(
        'aria-disabled',
        String(container.scrollLeft + container.clientWidth >= container.scrollWidth - 1),
      );
    };

    const scroll = (direction) => {
      const button = direction < 0 ? previous : next;
      if (button.getAttribute('aria-disabled') === 'true') return;
      container.scrollBy({
        left: direction * container.clientWidth,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    };

    const addControls = (button, direction) => {
      button.addEventListener('click', () => scroll(direction));
      button.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        scroll(direction);
      });
    };

    addControls(previous, -1);
    addControls(next, 1);
    container.addEventListener('scroll', updateButtons, { passive: true });
    updateButtons();
  }

  function activateStaticReviews(section) {
    const target = section.querySelector('[data-static-reviews-target]');
    const template = section.querySelector('[data-static-reviews-template]');
    if (!target || !template) return;

    const content = template.content.cloneNode(true);

    // Verwijder alleen de selectors waarmee de Judge.me-loader de zware carousel activeert.
    content.querySelectorAll('.jdgm-carousel').forEach((carousel) => {
      carousel.classList.remove('jdgm-carousel');
      carousel.classList.add('home-reviews-carousel');
    });
    content.querySelectorAll('.jdgm-widget').forEach((widget) => {
      widget.classList.remove('jdgm-widget');
    });

    removeInvalidReviews(content);
    content.querySelectorAll('.jdgm-carousel-item').forEach((card, index) => {
      if (index >= visibleCardCount) card.hidden = true;
    });

    target.appendChild(content);
    template.remove();
    loadVisibleImages(target);
    enhanceProductTitles(target, parseVendors(section));
    enhanceReviews(target, section);
    window.setTimeout(() => enhanceReviews(target, section), 700);
    enhanceSlider(target, section);
  }

  function init() {
    document.querySelectorAll(sectionSelector).forEach(activateStaticReviews);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
